import { Link } from 'react-router-dom';
import { Hero } from '../Hero.jsx';
import { AboutPinned } from '../AboutPinned.jsx';
import { Marquee } from '../Marquee.jsx';
import { MagneticLink } from '../MagneticLink.jsx';
import { EmailCopy } from '../EmailCopy.jsx';
import { Footer } from '../Footer.jsx';
import { ResponsivePicture } from '../ResponsivePicture.jsx';

export default function Home() {
  return (
    <>
      <Hero bgSrc="/images/home-hero.jpg" topLabel="Where Color Becomes Care" distortion={true}>
        <h1 className="hero-title">
          <span data-split>Not Just Art</span>
          <span className="dash">—</span>
          <span className="em" data-split>
            A Movement
          </span>
        </h1>
        <p className="hero-subtitle" data-hero-delay="1.2">
          Art, photography, and visual storytelling by Zongheng Brian He — a high school artist in
          Los Angeles capturing real emotions and everyday moments.
        </p>
        <div className="hero-meta" data-hero-delay="1.6">
          <span>Los Angeles · CA</span>
          <span>Artist · Designer · Founder</span>
        </div>
      </Hero>

      <AboutPinned />

      <Marquee items={['Art', 'Care', 'Color', 'Movement', 'Seen', 'Connection']} />

      {/* Feature teasers */}
      <section className="section">
        <article className="feature reveal">
          <Link
            className="feature-visual"
            to="/portfolio"
            viewTransition
            aria-label="View Art Galleries"
          >
            <ResponsivePicture
              src="/images/portfolio-hero.jpg"
              alt="Featured artwork"
              sizes="(max-width: 720px) 100vw, 50vw"
              viewTransitionName="portfolio-portal"
            />
          </Link>
          <div className="feature-body">
            <span className="tag feature-tag-red">01 — Portfolio</span>
            <h3 style={{ viewTransitionName: 'portfolio-title' }}>Art Galleries</h3>
            <p>
              Art is how I see and understand the world. Working across painting, digital art,
              photography, and mixed media, I use visual storytelling to capture emotion and
              transform fleeting moments into something lasting. I am drawn to the power of color —
              how it can shape perception, carry feeling, and speak without language.
            </p>
            <p>
              My work is a continuous exploration of perspective, identity, and the quiet details of
              everyday life.
            </p>
            <Link className="btn-ghost" to="/portfolio" viewTransition>
              Enter Gallery <span className="arrow">→</span>
            </Link>
          </div>
        </article>

        <article className="feature reveal">
          <Link className="feature-visual" to="/club" viewTransition aria-label="Visit Club Info">
            <ResponsivePicture
              src="/images/club-hero.jpg"
              alt="Color and Care community scene"
              sizes="(max-width: 720px) 100vw, 50vw"
              viewTransitionName="club-portal"
            />
          </Link>
          <div className="feature-body">
            <span className="tag feature-tag-red">02 — Color and Care</span>
            <h3 style={{ viewTransitionName: 'club-title' }}>Club Info</h3>
            <p>
              Beyond fine arts, I use art as a way to serve and connect with others through{' '}
              <em>Color and Care</em>. Through organizing events, collaborating with community
              organizations, and creating artwork, I aim to bring joy, comfort, and a sense of
              connection to those in need.
            </p>
            <p>
              For me, art is not only a form of expression, but also a way to give back and make a
              meaningful impact.
            </p>
            <Link className="btn-ghost" to="/club" viewTransition>
              Meet the Club <span className="arrow">→</span>
            </Link>
          </div>
        </article>

        <article className="feature reveal">
          <Link className="feature-visual" to="/brand" viewTransition aria-label="See Brand Story">
            <ResponsivePicture
              src="/images/brand-hero.jpg"
              alt="MADE TO BE SEEN brand"
              sizes="(max-width: 720px) 100vw, 50vw"
              viewTransitionName="brand-portal"
            />
          </Link>
          <div className="feature-body">
            <span className="tag feature-tag-red">03 — Made to Be Seen</span>
            <h3 style={{ viewTransitionName: 'brand-title' }}>Brand Story</h3>
            <p>
              <span className="mark-red">Made to Be Seen</span> is a concept-driven fashion brand
              that transforms artwork created by children with autism into wearable pieces, allowing
              their voices, perspectives, and beauty to be seen by the world.
            </p>
            <p>
              Each garment carries a story — a fragment of color, line, or texture from a
              child&rsquo;s hand — translated into clothing that moves through the world the way art
              rarely gets to. The brand exists where craft meets advocacy.
            </p>
            <Link className="btn-ghost" to="/brand" viewTransition>
              Discover the Brand <span className="arrow">→</span>
            </Link>
          </div>
        </article>

        <article className="feature reveal">
          <Link className="feature-visual" to="/contact" viewTransition aria-label="Contact">
            <ResponsivePicture
              src="/images/contact-hero.jpg"
              alt="Quiet moment by the water"
              sizes="(max-width: 720px) 100vw, 50vw"
              viewTransitionName="contact-portal"
            />
          </Link>
          <div className="feature-body">
            <span className="tag feature-tag-red">04 — Contact us</span>
            <h3 style={{ viewTransitionName: 'contact-title' }}>
              Let&rsquo;s make something together.
            </h3>
            <p>
              Beyond my creative work, I enjoy learning, collaborating, and exploring new
              environments — through academics, travel, and community engagement. I believe
              creativity has the power to inspire, connect, and bring people together.
            </p>
            <p>I&rsquo;m always open to new ideas and collaborations — feel free to reach out.</p>
            <MagneticLink to="/contact" viewTransition className="btn">
              Say Hello <span className="arrow">→</span>
            </MagneticLink>
          </div>
        </article>
      </section>

      <Footer
        bgImage="/images/home-footer.jpg"
        bottomRight="Not just art — a movement."
        distortion={true}
      >
        <h2>
          <span data-split>Where </span>
          <em data-split>color</em>
          <span data-split> becomes care.</span>
        </h2>
        <div className="footer-meta">
          <span>Studio</span>
          <EmailCopy />
          <span>Los Angeles, CA</span>
        </div>
      </Footer>
    </>
  );
}
